import { useEffect, useState } from "react";
import { api } from "@/lib/api";

let cache = null;
let pending = null;
let fetchedAt = 0;
const listeners = new Set();
const MIN_REFRESH_MS = 4000;

// Single client-side source for public platform status (/api/platform/status, never cached by the browser).
export const loadPlatform = (fresh = false) => {
  if (!fresh && cache) return Promise.resolve(cache);
  if (fresh && pending && Date.now() - fetchedAt < MIN_REFRESH_MS) return pending;
  fetchedAt = Date.now();
  pending = api.get("/platform/status", { params: { _: fetchedAt } })
    .then((r) => { cache = r.data; listeners.forEach((l) => l(cache)); return cache; })
    .catch(() => cache || {});
  return pending;
};

if (typeof window !== "undefined") {
  const recheck = () => { if (document.visibilityState !== "hidden") loadPlatform(true); };
  window.addEventListener("focus", recheck);
  document.addEventListener("visibilitychange", recheck);
  window.addEventListener("online", recheck);
}

export const usePlatform = () => {
  const [s, setS] = useState(cache);
  useEffect(() => { listeners.add(setS); loadPlatform(); return () => listeners.delete(setS); }, []);
  return s || {};
};

export const useCreatorOn = () => usePlatform().creator_platform_enabled === true;

// Maintenance screen state: shown only for code=="maintenance", re-checked every 5s while shown, cleared as soon as the server says OFF.
export const useMaintenance = (pathname) => {
  const p = usePlatform();
  const [hit, setHit] = useState(false);
  useEffect(() => {
    const on = () => { setHit(true); loadPlatform(true); };
    window.addEventListener("aetryx:maintenance", on);
    return () => window.removeEventListener("aetryx:maintenance", on);
  }, []);
  useEffect(() => { loadPlatform(true); }, [pathname]);
  useEffect(() => { if (p.maintenance === false) setHit(false); }, [p.maintenance]);
  const active = p.maintenance === true || (hit && p.maintenance !== false);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => loadPlatform(true), 5000);
    return () => clearInterval(id);
  }, [active]);
  return active;
};

export const PlatformBranding = () => {
  const p = usePlatform();
  useEffect(() => {
    if (!p.name) return;
    document.title = `${p.name} — ${p.tagline}`;
    if (p.icon_url) document.querySelectorAll("link[rel~='icon'],link[rel='apple-touch-icon']").forEach((l) => { l.href = p.icon_url; });
  }, [p.name, p.tagline, p.icon_url]);
  return null;
};
